import { Injectable, inject, OnDestroy } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppConfigService } from './app-config.service';
import { ISupportRequestMessage } from '../interfaces/player/ISupportRequestMessage';

@Injectable({
  providedIn: 'root'
})
export class SupportTicketSignalRService implements OnDestroy {
  private appConfigService = inject(AppConfigService);
  private hubConnection: signalR.HubConnection | null = null;
  private currentSupportRequestId: string | null = null;
  private currentAdminName: string | null = null;

  private newMessageSubject = new BehaviorSubject<ISupportRequestMessage | null>(null);
  public newMessage$: Observable<ISupportRequestMessage | null> = this.newMessageSubject.asObservable();

  /**
   * Connect to the SignalR hub and join the support ticket group as an admin.
   * @param supportRequestId The ID of the support request to subscribe to
   * @param adminName The name of the admin (for presence tracking)
   */
  public connect(supportRequestId: string, adminName: string): void {
    // If already connected to a different support request, disconnect first
    if (this.currentSupportRequestId && this.currentSupportRequestId !== supportRequestId) {
      this.disconnect();
    }

    // If already connected to this support request, do nothing
    if (this.currentSupportRequestId === supportRequestId && this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    this.currentSupportRequestId = supportRequestId;
    this.currentAdminName = adminName;
    const hubUrl = this.appConfigService.commonApiUrl();

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${hubUrl}/supportTicketHub`, { withCredentials: true })
      .withAutomaticReconnect()
      .build();

    this.hubConnection.start()
      .then(() => {
        console.log('Connected to SupportTicketHub');
        this.joinSupportTicketGroup(supportRequestId, adminName);
        this.subscribeToEvents();
      })
      .catch(err => {
        console.error('Failed to connect to SupportTicketHub:', err);
      });

    this.hubConnection.onreconnected(() => {
      console.log('Reconnected to SupportTicketHub');
      if (this.currentSupportRequestId && this.currentAdminName) {
        this.joinSupportTicketGroup(this.currentSupportRequestId, this.currentAdminName);
      }
    });
  }

  private joinSupportTicketGroup(supportRequestId: string, adminName: string): void {
    this.hubConnection?.invoke('AdminJoinSupportTicketGroup', supportRequestId, adminName)
      .catch(err => console.error('Error joining support ticket group:', err));
  }

  private subscribeToEvents(): void {
    this.hubConnection?.off('NewSupportMessage');
    this.hubConnection?.on('NewSupportMessage', (message: ISupportRequestMessage) => {
      console.log('Received new support message:', message);
      this.newMessageSubject.next(message);
    });
  }

  /**
   * Disconnect from the SignalR hub.
   */
  public disconnect(): void {
    if (this.hubConnection) {
      if (this.currentSupportRequestId) {
        this.hubConnection.invoke('LeaveSupportTicketGroup', this.currentSupportRequestId)
          .catch(err => console.error('Error leaving support ticket group:', err));
      }
      this.hubConnection.stop();
      this.hubConnection = null;
      this.currentSupportRequestId = null;
      this.currentAdminName = null;
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
